import { useQueries } from '@tanstack/react-query'

import { documentsApi } from '@/lib/api'
import type { DocumentCounts } from '@/types/domain'

import { createLimiter } from '@/lib/limit'
import { isBusy } from './overviewModel'

/**
 * The per-card document counts, fanned out at four requests at a time.
 *
 * One query per template rather than one query for all of them, so a card
 * whose count is already cached paints immediately and a single failing
 * template does not blank every other card's status line. The cache key is
 * shared with the rest of the app (`['documents', 'counts', id]`), so the
 * cockpit's own count query and this one are the same entry.
 *
 * A failure is deliberately quiet: the card falls back to its loading state
 * and the review banner leaves that template out. A red error strip over a
 * grid of forms, because a status line could not be fetched, would be the
 * wrong trade.
 */
const limiter = createLimiter(4)

export function useTemplateCounts(ids: string[]): Map<string, DocumentCounts> {
  return useQueries({
    queries: ids.map((id) => ({
      queryKey: ['documents', 'counts', id],
      queryFn: () => limiter(() => documentsApi.counts(id)),
      staleTime: 30_000,
      retry: false,
      // Only a template with documents in flight changes on its own; polling
      // the rest every half minute would be one request per card per poll for
      // numbers that cannot have moved.
      refetchInterval: (query: { state: { data?: DocumentCounts } }) =>
        isBusy(query.state.data) ? 30_000 : false,
    })),
    combine: (results) => {
      const counts = new Map<string, DocumentCounts>()
      results.forEach((result, index) => {
        if (result.data) counts.set(ids[index], result.data)
      })
      return counts
    },
  })
}
